import { motion } from "framer-motion";
import { Flame, Plus, Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";
import {
  WINGS_PRICING, TENDERS_PRICING, COMBO_NOTES,
  SIGNATURE_FLAVORS, DRY_RUBS, ADD_ONS, DRINKS, TAGLINE, IMAGES,
} from "../../data";

const HEAT_COLORS = ["#39FF14", "#39FF14", "#DFFF00", "#FF8A00", "#FF2D2D"];

const HeatMeter = ({ level }) => (
  <span className="flex items-center gap-1" aria-label={`heat level ${level} of 5`}>
    {[...Array(5)].map((_, i) => (
      <Flame
        key={i}
        size={16}
        style={{ color: i < level ? HEAT_COLORS[level - 1] : undefined }}
        className={i < level ? "" : "text-neutral-800"}
        fill={i < level ? "currentColor" : "none"}
      />
    ))}
  </span>
);

const PriceList = ({ title, tag, items, testid }) => (
  <div data-testid={testid} className="bg-black border border-neon-green/20 p-7">
    <div className="flex items-baseline justify-between mb-5">
      <h3 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-wide neon-glow-green">{title}</h3>
      <span className="font-graffiti text-neon-green text-2xl -rotate-2">{tag}</span>
    </div>
    <div className="flex flex-col gap-3">
      {items.map((it) => (
        <div key={it.size} className="flex items-end">
          <span className="font-body font-semibold text-base sm:text-lg text-neutral-200">{it.size}</span>
          <span className="dotted-leader" />
          <span className="font-heading text-2xl sm:text-3xl text-neon-yellow">{it.price}</span>
        </div>
      ))}
    </div>
  </div>
);

export const MenuSection = () => (
  <section id="menu" className="relative bg-surface px-5 sm:px-8 lg:px-12 py-24 sm:py-32 border-t border-neon-green/15">
    <div className="mx-auto max-w-[1600px]">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-14">
          <div>
            <span className="font-graffiti text-neon-yellow text-4xl sm:text-5xl -rotate-2 inline-block">real wings, real flavor</span>
            <h2 className="font-heading text-6xl sm:text-7xl lg:text-8xl text-neon-green uppercase tracking-tighter leading-none neon-glow-green">
              The Menu
            </h2>
          </div>
          <p className="font-heading text-2xl sm:text-3xl text-white uppercase tracking-wide max-w-sm sm:text-right">
            {TAGLINE}
          </p>
        </div>
      </Reveal>

      {/* Wings + Tenders pricing */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
        <Reveal><PriceList title="Wings" tag="Alitas" items={WINGS_PRICING} testid="wings-pricing" /></Reveal>
        <Reveal delay={0.08}><PriceList title="Tenders" tag="Tenders" items={TENDERS_PRICING} testid="tenders-pricing" /></Reveal>
      </div>

      {/* Combo notes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-20">
        {COMBO_NOTES.map((c, i) => (
          <Reveal key={c.label} delay={i * 0.08}>
            <div className="flex items-center gap-4 bg-black border border-neon-green/20 p-5">
              <Flame className="text-neon-green shrink-0" size={28} />
              <p className="font-body text-neutral-200">
                <span className="font-heading text-xl uppercase tracking-wide">{c.label}</span>{" "}
                <span className="text-neon-yellow font-heading text-xl uppercase">{c.accent}</span>
              </p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Signature Flavors */}
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-10">
          <h2 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-neon-yellow uppercase tracking-tighter leading-none neon-glow-yellow">
            Signature Flavors
          </h2>
          <div className="flex items-center gap-3 font-body text-xs uppercase tracking-[0.2em] text-neutral-400">
            <span className="text-neon-green">Less Spicy</span>
            <span className="h-1 w-24 rounded-full" style={{ background: "linear-gradient(90deg,#39FF14,#DFFF00,#FF8A00,#FF2D2D)" }} />
            <span className="text-red-500">Extreme</span>
          </div>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {SIGNATURE_FLAVORS.map((f, i) => (
            <Reveal key={f.name} delay={i * 0.04}>
              <div
                data-testid={`flavor-card-${f.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`group h-full flex flex-col bg-black border p-6 transition-colors duration-300 ${
                  f.heat === 5 ? "border-red-500/50 hover:border-red-500" : "border-neon-green/20 hover:border-neon-green"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  {f.isNew ? (
                    <span className="inline-flex items-center gap-1 font-heading text-sm tracking-widest bg-neon-yellow text-black px-2 py-0.5">
                      <Sparkles size={12} /> NEW
                    </span>
                  ) : (
                    <span className="font-body text-[10px] uppercase tracking-[0.25em] text-neutral-600">Flavor</span>
                  )}
                  <HeatMeter level={f.heat} />
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl text-white uppercase tracking-wide group-hover:text-neon-green transition-colors duration-300">
                  {f.name}
                </h3>
                <p className="font-body text-sm text-neutral-400 mt-2 leading-relaxed">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Elote feature image */}
        <Reveal delay={0.15}>
          <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.4 }} className="relative overflow-hidden border border-neon-green/25 h-full min-h-[440px] lg:sticky lg:top-28">
            <img src={IMAGES.elote} alt="67 Elote Blast wing sauce — back by popular demand" className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <span className="font-body text-xs uppercase tracking-[0.25em] text-neon-yellow">New · Back By Popular Demand</span>
              <h3 className="font-heading text-4xl sm:text-5xl text-white uppercase leading-none mt-1">67 Elote Blast</h3>
              <p className="font-body text-sm text-neutral-300 mt-2 max-w-xs">Creamy elote flavor with lime, cheese & a touch of chili. Non spicy — pure sabor.</p>
            </div>
          </motion.div>
        </Reveal>
      </div>

      {/* Dry Rubs */}
      <div id="sauces" className="mt-24 scroll-mt-24">
        <Reveal>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-10">
            <h2 className="font-heading text-5xl sm:text-6xl text-neon-yellow uppercase tracking-tighter leading-none neon-glow-yellow">Dry Rubs</h2>
            <span className="font-graffiti text-neon-green text-2xl sm:text-3xl -rotate-2">seasoned to perfection</span>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {DRY_RUBS.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.08}>
              <div data-testid={`dry-rub-${r.name.toLowerCase().replace(/\s/g, "-")}`} className="group h-full bg-black border border-neon-green/20 p-7 hover:border-neon-green transition-colors duration-300">
                <h3 className="font-heading text-3xl text-white uppercase tracking-wide group-hover:text-neon-green transition-colors duration-300">{r.name}</h3>
                <p className="font-body text-sm text-neutral-400 mt-2 leading-relaxed">{r.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Add-ons + Drinks */}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Reveal>
          <div data-testid="addons-list" className="bg-black border border-neon-green/20 p-7 h-full">
            <h3 className="font-heading text-3xl text-neon-green uppercase tracking-wide mb-5 flex items-center gap-2"><Plus size={22} /> Add-Ons</h3>
            <div className="flex flex-col gap-3">
              {ADD_ONS.map((a) => (
                <div key={a.name} className="flex items-end">
                  <span className="font-body text-base text-neutral-200">{a.name}</span>
                  <span className="dotted-leader" />
                  <span className="font-heading text-2xl text-neon-yellow">{a.price}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div data-testid="drinks-list" className="bg-black border border-neon-green/20 p-7 h-full">
            <h3 className="font-heading text-3xl text-neon-green uppercase tracking-wide mb-5">Drinks</h3>
            <div className="flex flex-col gap-3">
              {DRINKS.map((d) => (
                <div key={d.name} className="flex items-end">
                  <span className="font-body text-base text-neutral-200">{d.name}</span>
                  <span className="dotted-leader" />
                  <span className="font-heading text-2xl text-neon-yellow">{d.price}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default MenuSection;
